<template>
  <section>
    <scroll-container
      class="pt-2 mt-n30px"
      :height-offset="isInEditMode ? '55px' : '125px'"
    >
      <b-row v-if="loading">
        <b-col
          sm="12"
          class="text-center"
        >
          <b-skeleton
            class="d-inline-block"
            animation="wave"
            width="40%"
            height="300px"
          />
        </b-col>
      </b-row>
      <b-container
        v-else
        class="container-small"
      >
        <b-card class="mb-2">
          <div class="mb-2">
            <b-card-title
              v-if="!isInEditMode"
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.NewDisplayAdImage') }}
            </b-card-title>
            <b-card-title
              v-else
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.EditDisplayAdImage') }}
            </b-card-title>
            <b-breadcrumb class="m-0 p-0">
              <b-breadcrumb-item :to="`/${$route.params.accountId}/campaign/edit/${wrapper.campaignModel.id}`">
                {{ wrapper.campaignModel.name }}
              </b-breadcrumb-item>
              <b-breadcrumb-item :to="`/${$route.params.accountId}/adgroup/edit?adGroupId=${wrapper.adGroup.id}&campaignId=${wrapper.campaignModel.id}`">
                {{ wrapper.adGroup.name }}
              </b-breadcrumb-item>
              <b-breadcrumb-item
                v-if="!isInEditMode"
                active
              >
                {{ $tk('ClientPortal.Form.NewAd') }}
              </b-breadcrumb-item>
              <b-breadcrumb-item
                v-else
                active
              >
                {{ wrapper.displayAdEdit.adName }}
              </b-breadcrumb-item>
            </b-breadcrumb>
            <hr class="m-0 mt-1">
          </div>
          <b-form-group
            class="mb-0"
            :label="$tk('ClientPortal.Form.SelectFiles(ThisFileShouldNotBeLargerThan4MB)')"
            label-size="sm"
            label-class="pb-50"
          >
            <vue-dropzone
              id="dropzone"
              ref="imageDropzone"
              :use-custom-slot="true"
              :options="dropzoneOptions"
              @vdropzone-success="uploadFile"
              @vdropzone-sending="beforeSendingFile"
              @vdropzone-processing="dropzoneChangeUrl"
              @vdropzone-thumbnail="checkFileThumbnail"
              @vdropzone-complete="removeFile"
            >
              <div class="dz-default dz-message dz-message-center mx-3">
                <span class="d-block mb-3 line-height-1">
                  {{ $tk('ClientPortal.Form.UploadFilesWhichContainTheseFormats') }}
                  <span
                    v-for="item in wrapper.adFormats"
                    :key="item.id"
                    class="format-type"
                  >{{ item.name }}
                  </span>
                  <!-- 300x250 170x600 728x90 300x600 970x250 800x400 705x230 320x40 300x300 -->
                </span>
                <div>
                  <b-button
                    variant="primary"
                    size="sm"
                    class="mr-50 mb-50 mb-sm-0"
                  >
                    <GjIcon
                      class="mr-50"
                      name="Hyperlink"
                    />
                    {{ $tk('ClientPortal.Form.UploadFiles') }}
                  </b-button>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="showGallery($event)"
                  >
                    <GjIcon
                      class="mr-50"
                      name="Image"
                    />
                    {{ $tk('ClientPortal.Form.ChooseFromCreatives') }}
                  </b-button>
                </div>
              </div>
            </vue-dropzone>
            <span
              v-if="uploadMessage"
              :class="(uploadMessage.results.filter(el => el.inserted === false).length) === 0 && uploadMessage.results.length !== 0 ? 'text-success' : 'text-danger'"
              class="field-validation-valid small"
            >
              {{ uploadMessage.message }}
            </span>
          </b-form-group>
        </b-card>
        <div v-if="isInEditMode">
          <b-card>
            <b-form-group
              class="mb-2"
              :label="$tk('ClientPortal.Form.FileName')"
            >
              <b-form-row>
                <b-col sm="11">
                  <b-form-input
                    v-model="wrapper.displayAdEdit.fileName"
                    type="text"
                    placeholder="300x250.jpg"
                    disabled
                  />
                </b-col>
              </b-form-row>
            </b-form-group>
            <b-form-group
              class="mb-2"
              :label="$tk('ClientPortal.Form.AdName') + ' *'"
              :invalid-feedback="wrapper.displayAdEdit.errors['AdName']"
              :state="wrapper.displayAdEdit.errors['AdName'] == null ? null : false"
            >
              <b-form-row>
                <b-col sm="11">
                  <AdNameField
                    :id="'ad-name-'+wrapper.displayAdEdit.id"
                    v-model="wrapper.displayAdEdit.adName"
                    :variables="adNameVariables"
                    :placeholder="$tk('ClientPortal.Form.AdNamePlaceholder')"
                    :state="wrapper.displayAdEdit.errors['AdName'] == null ? null : false"
                  />
                </b-col>
                <b-col
                  class="pt-50 text-center"
                  sm="1"
                  @click="copyText(wrapper.displayAdEdit.id,'name')"
                >
                  <GjIcon
                    class="cursor-pointer"
                    name="Copy"
                  />
                </b-col>
              </b-form-row>
            </b-form-group>
            <b-form-group
              class="mb-2"
              :label="$tk('ClientPortal.Form.Url') + ' *'"
              :invalid-feedback="wrapper.displayAdEdit.errors['Url']"
              :state="wrapper.displayAdEdit.errors['Url'] == null ? null : false"
            >
              <b-form-row>
                <b-col sm="11">
                  <b-form-input
                    :id="'ad-url-'+wrapper.displayAdEdit.id"
                    v-model="wrapper.displayAdEdit.url"
                    type="text"
                    placeholder="https://www.example.com/page"
                    :state="wrapper.displayAdEdit.errors['Url'] == null ? null : false"
                  />
                </b-col>
                <b-col
                  class="pt-50 text-center"
                  sm="1"
                  @click="copyText(wrapper.displayAdEdit.id,'url')"
                >
                  <GjIcon
                    class="cursor-pointer"
                    name="Copy"
                  />
                </b-col>
              </b-form-row>
            </b-form-group>
            <b-button
              v-b-toggle.url-builder
              class="bg-white mb-2"
              size="sm"
              variant="outline-primary"
            >
              <GjIcon
                class="mr-50"
                name="Hyperlink"
              />
              <span class="when-open">
                {{ $tk('ClientFormat.Form.CloseUrlBuilder') }}
                <GjIcon
                  class="mr-50"
                  name="ArrowUp"
                />
              </span>
              <span class="when-closed">
                {{ $tk('ClientFormat.Form.OpenUrlBuilder') }}
                <GjIcon
                  class="mr-50"
                  name="ArrowDown"
                />
              </span>
            </b-button>
            <b-collapse
              id="url-builder"
            >
              <url-builder
                v-model="wrapper.displayAdEdit.url"
                :options="wrapper.utmOptions"
              />
            </b-collapse>
            <b-form-group
              class="mb-0"
            >
              <legend class="col-form-label pt-0">
                {{ $tk('ClientPortal.Form.ImpressionTag') }}
                <GjIcon
                  v-b-tooltip.hover.bottom="$tk('ClientPortal.Form.ImpressionTagTooltip')"
                  name="Info"
                />
              </legend>
              <b-form-row>
                <b-col sm="11">
                  <b-form-input
                    :id="'ad-imp-'+wrapper.displayAdEdit.id"
                    v-model="wrapper.displayAdEdit.impressionTag"
                    type="text"
                    :placeholder="$tk('ClientPortal.Form.ImpressionTagPlaceholder')"
                  />
                </b-col>
                <b-col
                  class="pt-50 text-center"
                  sm="1"
                  @click="copyText(wrapper.displayAdEdit.id,'imp')"
                >
                  <GjIcon
                    class="cursor-pointer"
                    name="Copy"
                  />
                </b-col>
              </b-form-row>
            </b-form-group>
          </b-card>

          <b-card class="preview-image-ad">
            <b-card-title
              v-b-toggle="`collapse-ad-preview-${wrapper.displayAdEdit.id}`"
              class="d-flex justify-content-between p-2 mb-0"
            >
              <div
                class="text-primary d-flex align-items-center"
              >
                <GjIcon name="Show" />
                {{ $tk('ClientPortal.Form.AdCreation.AdPreview') }}
              </div>
              <span class="when-open">
                <GjIcon name="ArrowUp" />
              </span>
              <span class="when-closed">
                <GjIcon name="ArrowDown" />
              </span>
            </b-card-title>
            <b-collapse
              :id="`collapse-ad-preview-${wrapper.displayAdEdit.id}`"
            >
              <PreviewImageAd
                :preview-ad="wrapper.displayAdEdit.show"
                :url="wrapper.displayAdEdit.mediaUrl"
              />
            </b-collapse>
          </b-card>
        </div>
        <template v-else>
          <div
            v-for="(displayAd, index) in wrapper.displayAds"
            :key="index"
            class="mb-2"
          >
            <b-card>
              <b-form-group
                class="mb-2"
                :label="$tk('ClientPortal.Form.FileName')"
              >
                <b-form-row>
                  <b-col sm="11">
                    <b-form-input
                      v-model="displayAd.fileName"
                      type="text"
                      placeholder="300x250.jpg"
                      disabled
                    />
                  </b-col>
                  <b-col
                    class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                    sm="1"
                  >
                    <b-button
                      variant="link"
                      size="sm"
                      @click="removeDisplayAd(displayAd.id)"
                    >
                      <GjIcon
                        class="cursor-pointer text-secondary"
                        name="Delete"
                      />
                    </b-button>
                  </b-col>
                </b-form-row>
              </b-form-group>
              <b-form-group
                class="mb-2"
                :label="$tk('ClientPortal.Form.AdName') + ' *'"
                :invalid-feedback="displayAd.errors['AdName']"
                :state="displayAd.errors['AdName'] == null ? null : false"
              >
                <b-form-row>
                  <b-col sm="11">
                    <AdNameField
                      :id="'ad-name-'+displayAd.id"
                      v-model="displayAd.adName"
                      :variables="adNameVariables"
                      :placeholder="$tk('ClientPortal.Form.AdNamePlaceholder')"
                      :state="displayAd.errors['AdName'] == null ? null : false"
                    />
                  </b-col>
                  <b-col
                    class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                    sm="1"
                    @click="copyText(displayAd.id,'name')"
                  >
                    <GjIcon
                      class="cursor-pointer"
                      name="Copy"
                    />
                  </b-col>
                </b-form-row>
              </b-form-group>
              <b-form-group
                class="mb-2"
                :label="$tk('ClientPortal.Form.Url') + ' *'"
                :invalid-feedback="displayAd.errors['Url']"
                :state="displayAd.errors['Url'] == null ? null : false"
              >
                <b-form-row>
                  <b-col sm="11">
                    <b-form-input
                      :id="'ad-url-'+displayAd.id"
                      v-model="displayAd.url"
                      type="text"
                      placeholder="https://www.example.com/page"
                      :state="displayAd.errors['Url'] == null ? null : false"
                    />
                  </b-col>
                  <b-col
                    class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                    sm="1"
                    @click="copyText(displayAd.id,'url')"
                  >
                    <GjIcon
                      class="cursor-pointer"
                      name="Copy"
                    />
                  </b-col>
                </b-form-row>
              </b-form-group>
              <b-button
                v-b-toggle.url-builder
                class="bg-white mb-2"
                size="sm"
                variant="outline-primary"
              >
                <GjIcon
                  class="mr-50"
                  name="Hyperlink"
                />
                <span class="when-open">
                  {{ $tk('ClientFormat.Form.CloseUrlBuilder') }}
                  <GjIcon
                    class="mr-50"
                    name="ArrowUp"
                  />
                </span>
                <span class="when-closed">
                  {{ $tk('ClientFormat.Form.OpenUrlBuilder') }}
                  <GjIcon
                    class="mr-50"
                    name="ArrowDown"
                  />
                </span>
              </b-button>
              <b-collapse
                id="url-builder"
              >
                <url-builder
                  :ref="'utm'+index"
                  :key="index"
                  v-model="displayAd.url"
                  :options="wrapper.utmOptions"
                />
              </b-collapse>
              <b-form-group
                class="mb-0"
              >
                <legend class="col-form-label pt-0">
                  {{ $tk('ClientPortal.Form.ImpressionTag') }}
                  <GjIcon
                    v-b-tooltip.hover.bottom="$tk('ClientPortal.Form.ImpressionTagTooltip')"
                    name="Info"
                  />
                </legend>
                <b-form-row>
                  <b-col sm="11">
                    <b-form-input
                      :id="'ad-imp-'+displayAd.id"
                      v-model="displayAd.impressionTag"
                      type="text"
                      :placeholder="$tk('ClientPortal.Form.ImpressionTagPlaceholder')"
                    />
                  </b-col>
                  <b-col
                    class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                    sm="1"
                    @click="copyText(displayAd.id,'imp')"
                  >
                    <GjIcon
                      class="cursor-pointer"
                      name="Copy"
                    />
                  </b-col>
                </b-form-row>
              </b-form-group>
            </b-card>
            <b-card class="preview-image-ad">
              <b-card-title
                v-b-toggle="`collapse-ad-preview-${displayAd.id}`"
                class="d-flex justify-content-between p-2 mb-0"
              >
                <div
                  class="text-primary d-flex align-items-center"
                >
                  <GjIcon name="Show" />
                  {{ $tk('ClientPortal.Form.AdCreation.AdPreview') }}
                </div>
                <span class="when-open">
                  <GjIcon name="ArrowUp" />
                </span>
                <span class="when-closed">
                  <GjIcon name="ArrowDown" />
                </span>
              </b-card-title>
              <b-collapse
                :id="`collapse-ad-preview-${displayAd.id}`"
              >
                <PreviewImageAd
                  :preview-ad="displayAd.show"
                  :url="displayAd.mediaUrl"
                />
              </b-collapse>
            </b-card>
          </div>
        </template>
      </b-container>
    </scroll-container>

    <div class="border-top text-right py-1">
      <div class="container-small mx-auto">
        <b-button
          v-if="isInEditMode"
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="saving"
          @click="closeEditModal"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          v-else
          :to="`/${$route.params.accountId}`"
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="saving"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          class="bg-white ml-50"
          variant="primary"
          size="sm"
          :disabled="saving || !isFormComplete"
          @click="saveDisplayAd()"
        >
          {{ isInEditMode ? $tk('ClientPortal.Form.Actions.Save') : $tk('ClientPortal.Form.Actions.CreateAd') }}
        </b-button>
      </div>
    </div>
    <b-modal
      id="gallery-modal"
      ref="galleryModal"
      body-class="modal-body--no-padding creatives-modal"
      hide-footer
      centered
      :title="$tk('ClientPortal.Form.Gallery')"
      size="xl"
    >
      <loader
        v-if="galleryLoading"
        :size="24"
        :show="true"
        class="gallery_loader"
      />
      <div v-else>
        <div class="d-block mx-1 mb-1">
          <b-form-input
            v-model="creativeSearchTerm"
            type="text"
            :placeholder="$tk('ClientPortal.Form.SearchCreative')"
            @input="searchCreative"
          />
        </div>
        <hr class="mb-0">
        <scroll-container
          class="pt-1 px-1"
          height-offset="260px"
        >
          <b-row class="mx-n50">
            <b-col
              v-for="(creativeItem, index) in creatives"
              :key="index"
              xl="2"
              md="3"
              sm="6"
              cols="12"
              class="mb-1 px-50"
            >
              <b-card
                class="mb-0 h-100 border"
                :class="{'border-primary' : creativeItem.selected}"
                @click.prevent="selectCreative(creativeItem)"
              >
                <div class="text-right">
                  <b-form-radio
                    v-if="isInEditMode"
                    v-model="creativeItem.selected"
                    value="true"
                    name="creative"
                  />
                  <b-form-checkbox
                    v-else
                    v-model="creativeItem.selected"
                    class="d-inline-block pl-1"
                  />
                </div>
                <b-card
                  class="mb-2"
                  body-class="p-0"
                >
                  <div
                    class="products-img text-center mx-auto"
                    :style="{ backgroundImage: 'url(' + creativeItem.url + ')' }"
                  />
                </b-card>
                <b-card-title class="mb-0">
                  {{ creativeItem.name }}
                </b-card-title>
              </b-card>
            </b-col>
            <span
              v-if="creatives.length === 0"
              class="gallery-text"
            >
              <div>
                <GjIcon
                  size="64px"
                  name="Grid"
                />
              </div>
              <div>
                <span v-if="creativeSearchTerm.length > 0"> {{ $tk('ClientPortal.CompanionAds.NoCreativesAreFoundWithThatName') }}</span>
                <span v-else> {{ $tk('ClientPortal.CompanionAds.NoCreativesFound') }}</span>
              </div>
            </span>
          </b-row>
          <InfiniteLoading
            v-if="creatives.length > 0"
            spinner="spiral"
            @infinite="loadCreatives($event)"
          >
            <div slot="no-results" />
            <div slot="no-more" />
          </InfiniteLoading>
        </scroll-container>
        <div class="p-1 border-top text-right">
          <b-button
            class=""
            variant="outline-secondary"
            size="sm"
            @click.prevent="hideGallery"
          >
            {{ $tk('ClientPortal.Form.Actions.Cancel') }}
          </b-button>
          <b-button
            class="ml-50"
            variant="primary"
            size="sm"
            @click.prevent="addCreatives()"
          >
            {{ $tk('ClientPortal.Form.Add') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import Vue from 'vue'
import notification from '@/mixins/toastification'
import sweetAlert from '@/mixins/sweetAlert'
import ScrollContainer from '@/components/ScrollContainer.vue'
import UrlBuilder from '@/components/UrlBuilder.vue'
import InfiniteLoading from 'vue-infinite-loading'
import getAPIDomain from '@/utils/apiDomain/apiDomain'
import AdsService from '@/api/client/ads.service'
import GalleryService from '@/api/client/gallery.service'
import PreviewImageAd from '@/components/PreviewImageAd.vue'
import loader from '@/components/Loader.vue'
import scrollToError from '@/mixins/scrollToError'
import AdNameField from '@/components/AdNameField.vue'
import { AD_NAME_VARIABLES, findUnknownTokens } from '@/utils/adNameTokens'

const ADSUBTYPE_DISPLAY_IMAGE = 1
const IMAGE_CREATIVE_TYPE = 1
export default {
  inject: ['closeEditModal'],
  components: {
    GjIcon,
    vueDropzone: vue2Dropzone,
    ScrollContainer,
    UrlBuilder,
    InfiniteLoading,
    PreviewImageAd,
    loader,
    AdNameField,
  },
  mixins: [notification, sweetAlert],
  props: {
    // eslint-disable-next-line vue/require-default-prop
    adId: Number,
    // eslint-disable-next-line vue/require-default-prop
    adGroupId: Number,
    // eslint-disable-next-line vue/require-default-prop
    catalogSetId: {
      type: String,
    },
    saving: {
      type: Boolean,
    },
  },
  data() {
    return {
      wrapper: {},
      count: 1,
      uploadMessage: '',
      dropzoneOptions: {
        uploadMultiple: true,
        parallelUploads: 5,
        acceptedFiles: '.jpg,.png,.jpeg,.gif',
        url: 'url',
        thumbnailWidth: null,
        timeout: 60000,
        thumbnailHeight: null,
        addRemoveLinks: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      },
      creatives: [],
      creativePage: 0,
      creativeSearchTerm: '',
      galleryLoading: false,
      loading: false,
      items: [
        {
          text: 'Campaign Name',
          href: '#',
        },
        {
          text: 'AdGroup Name',
          href: '#',
        },
        {
          text: 'Ad Name',
          active: true,
        },
      ],
    }
  },
  computed: {
    isFormComplete() {
      if (this.isInEditMode) {
        const { displayAdEdit } = this.wrapper
        return displayAdEdit && displayAdEdit.adName && displayAdEdit.url && displayAdEdit.fileName
      }
      const { displayAds } = this.wrapper
      return displayAds && displayAds.length && displayAds.every(x => x.adName && x.url && x.fileName)
    },
    isInEditMode() {
      return this.adId > 0
    },
    adNameVariables() {
      return AD_NAME_VARIABLES
    },
  },
  async mounted() {
    this.loading = true
    try {
      this.wrapper = await AdsService.getAd(this.adId, this.adGroupId, this.catalogSetId, this.$route.params.accountId)
      if (this.wrapper.displayAdEdit && this.wrapper.displayAdEdit.mediaUrl) {
        this.wrapper.displayAdEdit.show = true
      }
    } catch (error) {
      this.error(this.$tk('ClientPortal.Form.FailedToLoadData'))
    } finally {
      this.loading = false
    }
  },
  methods: {
    setSaving(value) {
      this.$emit('onUpdate', value)
    },
    dropzoneChangeUrl() {
      const clientApi = getAPIDomain()

      const { accountId } = this.$route.params
      this.$refs.imageDropzone.setOption('url', `${clientApi}/upload/ad?subtype=${ADSUBTYPE_DISPLAY_IMAGE}&accountId=${accountId}`)
    },
    // async showDisplayAd(displayAd) {
    //   if (displayAd.show) {
    //     displayAd.show = false
    //   } else {
    //     displayAd.show = true
    //     try {
    //       const response = await AdsService.sanitazeUrl(displayAd.url)
    //       displayAd.url = response
    //     } catch (error) {
    //       this.error(error.message)
    //     }
    //   }
    // },

    removeDisplayAd(id) {
      const index = this.wrapper.displayAds.findIndex(x => x.id === id)
      if (index > -1) {
        this.wrapper.displayAds.splice(index, 1)
      }
    },
    copyText(id, type) {
      const element = `ad-${type}-${id}`
      const copyText = document.getElementById(element)
      copyText.select()
      copyText.setSelectionRange(0, 99999)
      document.execCommand('copy')
    },
    validateAdName(ad) {
      if (!ad.errors) this.$set(ad, 'errors', {})
      this.$set(ad.errors, 'AdName', null)
      const unknown = findUnknownTokens(ad.adName)
      if (unknown.length > 0) {
        const tokens = unknown.map(k => `{{${k}}}`).join(', ')
        this.$set(ad.errors, 'AdName', `Unknown variables: ${tokens}`)
        return false
      }
      return true
    },
    async saveDisplayAd() {
      this.setSaving(true)
      if (this.isInEditMode) {
        if (!this.validateAdName(this.wrapper.displayAdEdit)) {
          this.setSaving(false)
          this.$nextTick(() => scrollToError(this.wrapper.displayAdEdit.errors))
          return
        }
        const { entity } = this.$parent.$parent.$parent
        this.escapeImpressionTag([this.wrapper.displayAdEdit])
        try {
          const response = await AdsService.editDisplayAd(this.wrapper.displayAdEdit, this.$route.params.accountId)
          if (response.updated) {
            const resolvedName = response.adName || (response.displayAdModel && response.displayAdModel.adName) || this.wrapper.displayAdEdit.adName
            this.wrapper.displayAdEdit.adName = resolvedName
            entity.name = resolvedName
            this.success(response.message)
            this.closeEditModal()
          } else {
            this.error(response.message)
            this.wrapper.displayAdEdit = response.displayAdModel
            this.$nextTick(() => {
              scrollToError(this.wrapper.displayAdEdit.errors)
            })
          }
        } catch (error) {
          this.error(error.message)
        } finally {
          this.setSaving(false)
        }
      } else if (this.wrapper.displayAds.length > 0) {
        let hasInvalid = false
        this.wrapper.displayAds.forEach(ad => {
          if (!this.validateAdName(ad)) hasInvalid = true
        })
        if (hasInvalid) {
          this.$nextTick(() => {
            const firstInvalidInput = document.querySelector('.is-invalid')
            if (firstInvalidInput) {
              firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          })
          this.setSaving(false)
          return
        }
        this.escapeImpressionTag(this.wrapper.displayAds)
        try {
          const response = await AdsService.createDisplayAd(ADSUBTYPE_DISPLAY_IMAGE, this.wrapper.displayAds, this.$route.params.accountId)
          if (response.saved) {
            this.wrapper.displayAds = []
            this.sweetAlertAfterAdCreation()
          } else {
            this.wrapper.displayAds = response.displayAds
            this.error(this.$tk('ClientPortal.Ads.ErrorSavingChanges'))
            this.$nextTick(() => {
              const firstInvalidInput = document.querySelector('.is-invalid')
              if (firstInvalidInput) {
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }
            })
            this.setSaving(false)
          }
        } catch (error) {
          this.error(error.message)
          this.setSaving(false)
        }
      }
    },

    escapeImpressionTag(displayAds) {
      displayAds.forEach(element => {
        element.impressionTag = encodeURIComponent(element.impressionTag)
      })
    },
    fileFormatCheck(height, width) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.wrapper.adFormats.length; i++) {
        if (this.wrapper.adFormats[i].width === width && this.wrapper.adFormats[i].height === height) return true
      }
      return false
    },
    checkFileThumbnail(file) {
      this.setSaving(true)
      const imageFormatExists = this.fileFormatCheck(file.height, file.width)
      if (imageFormatExists === true) {
        // eslint-disable-next-line no-undef
        file.acceptDimensions = () => done
      }
    },
    beforeSendingFile(file, xhr, formData) {
      formData.append('file', file)
    },
    uploadFile(file, response) {
      this.uploadMessage = ''
      const imageFormatExists = this.fileFormatCheck(file.height, file.width)
      if (imageFormatExists === true) {
        response.results.forEach(element => {
          if (element.inserted) {
            if (this.isInEditMode) {
              this.wrapper.displayAdEdit.mediaUrl = element.imageLink
              this.wrapper.displayAdEdit.formatId = element.formatId
              this.wrapper.displayAdEdit.fileWidth = file.width
              this.wrapper.displayAdEdit.fileHeight = file.height
              this.wrapper.displayAdEdit.fileName = file.name
            } else {
              const index = this.wrapper.displayAds.findIndex(x => x.mediaUrl === element.imageLink)
              if (index === -1) {
                this.wrapper.displayAds.push({
                  id: this.count,
                  name: file.name,
                  adName: '',
                  fileWidth: file.width,
                  fileHeight: file.height,
                  fileName: element.fileName,
                  mediaUrl: element.imageLink,
                  url: '',
                  formatId: element.formatId,
                  adGroupId: this.wrapper.adGroup.id,
                  impressionTag: '',
                  show: true,
                  errors: {},
                })
                // eslint-disable-next-line no-plusplus
                this.count++
              }
            }
          }
        })
      }
      if (!response.success) this.uploadMessage = response
      this.setSaving(false)
    },
    removeFile(file) {
      this.$refs.imageDropzone.removeFile(file)
    },
    showGallery(event) {
      event.stopPropagation()
      this.loadCreatives(null, true)
      this.$refs.galleryModal.show()
    },
    hideGallery() {
      this.$refs.galleryModal.hide()
    },
    async loadCreatives($state, load = false) {
      if (load) { this.galleryLoading = true }
      const filterModel = {
        page: this.creativePage,
        type: IMAGE_CREATIVE_TYPE,
        searchTerm: this.creativeSearchTerm,
        allowedFormats: this.wrapper.adFormats || [],
      }
      try {
        const response = await GalleryService.getCreatives(filterModel, this.$route.params.accountId)
        if (response.length) {
          this.creativePage += 1
          this.creatives.push(...response)
          if ($state) $state.loaded()
        } else if ($state) $state.complete()
      } catch (error) {
        throw new Error(error)
      }
      this.galleryLoading = false
    },
    searchCreative() {
      Vue.set(this, 'creatives', [])
      this.creativePage = 0
      this.loadCreatives()
    },
    selectCreative(creative) {
      if (this.isInEditMode) {
        this.creatives.forEach(x => { x.selected = false })
      }
      creative.selected = !creative.selected
    },
    addCreatives() {
      if (this.isInEditMode) {
        const selectedCreative = this.creatives.find(x => x.selected === true)
        this.wrapper.displayAdEdit.mediaUrl = selectedCreative.url
        this.wrapper.displayAdEdit.fileName = selectedCreative.name
      } else {
        const selectedCreatives = this.creatives.filter(x => x.selected === true)

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < selectedCreatives.length; i++) {
          this.wrapper.displayAds.push({
            id: this.count,
            name: selectedCreatives[i].name,
            adName: '',
            fileWidth: selectedCreatives[i].width,
            fileHeight: selectedCreatives[i].height,
            fileName: selectedCreatives[i].name,
            mediaUrl: selectedCreatives[i].url,
            url: '',
            formatId: selectedCreatives[i].formatId,
            adGroupId: this.wrapper.adGroup.id,
            impressionTag: '',
            show: true,
            errors: {},
          })
          // eslint-disable-next-line no-plusplus
          this.count++
          selectedCreatives[i].selected = false
        }
      }

      this.hideGallery()
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/views/client/ads-manager/create/ads/image-ad.scss';
</style>
